import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import api from '../services/authService';

export const useAdminSecurity = (initialFilters = {}) => {
  const [filters, setFilters] = useState({
    activity_type: '',
    user_id: '',
    severity: '',
    date_from: '',
    date_to: '',
    ...initialFilters
  });

  const activityTypeOptions = [
    { value: '', label: 'All Types' },
    { value: 'login', label: 'Login' },
    { value: 'login_failed', label: 'Failed Login' },
    { value: 'logout', label: 'Logout' },
    { value: 'password_change', label: 'Password Change' },
    { value: 'password_reset', label: 'Password Reset' },
    { value: 'email_verification', label: 'Email Verification' },
    { value: 'two_factor_enabled', label: '2FA Enabled' },
    { value: 'two_factor_disabled', label: '2FA Disabled' },
    { value: 'account_locked', label: 'Account Locked' },
    { value: 'account_unlocked', label: 'Account Unlocked' },
    { value: 'suspicious_activity', label: 'Suspicious Activity' },
    { value: 'security_alert', label: 'Security Alert' }
  ];

  const severityOptions = [
    { value: '', label: 'All Severities' },
    { value: 'high', label: 'High' },
    { value: 'medium', label: 'Medium' },
    { value: 'low', label: 'Low' }
  ];

  const cleanFilters = useMemo(() => {
    return Object.fromEntries(
      Object.entries(filters).filter(([_, value]) => value !== '' && value !== null && value !== undefined)
    );
  }, [filters]);

  const queryString = new URLSearchParams(cleanFilters).toString();

  const { data: securityLogsResponse, isLoading, error } = useQuery({
    queryKey: ['adminSecurity', cleanFilters],
    queryFn: async () => {
      const response = await api.get(`/admin/security${queryString ? `?${queryString}` : ''}`);
      return response.data;
    }
  });

  // Fetch statistics
  const { data: statsResponse, isLoading: isLoadingStats } = useQuery({
    queryKey: ['adminSecurityStats'],
    queryFn: async () => {
      const response = await api.get('/admin/security/statistics');
      return response.data;
    }
  });

  const securityLogs = useMemo(() => {
    const data = securityLogsResponse?.data?.data || securityLogsResponse?.data;
    if (data && Array.isArray(data)) {
      return data;
    }
    if (data && data.data && Array.isArray(data.data)) {
      return data.data;
    }
    return [];
  }, [securityLogsResponse]);

  const statistics = useMemo(() => {
    return statsResponse?.data || {};
  }, [statsResponse]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleSearch = (data, searchTerm) => {
    if (!searchTerm) return data;
    
    return data.filter(log => 
      log.activity_description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      log.ip_address?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      log.user?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      log.user?.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      log.activity_type?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  const getSeverityColor = (activityType) => {
    const highSeverity = ['login_failed', 'account_locked', 'suspicious_activity', 'security_alert'];
    const mediumSeverity = ['password_change', 'password_reset', 'two_factor_enabled', 'two_factor_disabled'];
    
    if (highSeverity.includes(activityType)) {
      return 'bg-red-100 text-red-800';
    } else if (mediumSeverity.includes(activityType)) {
      return 'bg-yellow-100 text-yellow-800';
    }
    return 'bg-green-100 text-green-800';
  };

  return {
    securityLogs,
    isLoading: isLoading || isLoadingStats,
    error,
    filters,
    activityTypeOptions,
    severityOptions,
    handleFilterChange,
    handleSearch,
    getSeverityColor,
    statistics
  };
};

